class SplitMegaMenu extends HTMLElement {
  connectedCallback() {
    this.menus = Array.from(this.querySelectorAll(".split-nav-tab"));
    this.menus.forEach((menu) => this.bindMenu(menu));

    // Closing on leave is handled at the wrapper level (see onWrapperMouseLeave), not per-tab,
    // so hovering the gap between tabs doesn't prematurely close the currently active one.
    this.onWrapperMouseLeave = () => this.closeAllMenus();
    this.addEventListener("mouseleave", this.onWrapperMouseLeave);
  }

  disconnectedCallback() {
    this.menus?.forEach((menu) => this.unbindMenu(menu));
    this.removeEventListener("mouseleave", this.onWrapperMouseLeave);
  }

  closeAllMenus() {
    if (!this.isDesktop()) return;
    this.menus.forEach((menu) => {
      menu.classList.remove("active");
      menu.querySelector(".menu-tab__title")?.setAttribute("aria-expanded", "false");
      menu._submenuList?.submenuItems.forEach((item) => item.classList.remove("active"));
    });
  }

  isDesktop() {
    return window.matchMedia("(min-width: 990px)").matches;
  }

  bindMenu(menu) {
    const menuHeader = menu.querySelector(".menu-tab");
    const menuLink = menu.querySelector(".menu-tab__title");
    const backButton = menu.querySelector(":scope > .menu-content > .menu-content__back");
    const hasDropdown =
      menu.querySelector(".mega-menu__list-item, .mega-menu__promo-card") !== null;

    this.bindSubmenuList(menu);

    if (menuLink) {
      menuLink.setAttribute("aria-haspopup", "true");
      menuLink.setAttribute("aria-expanded", "false");
    }

    menu._splitMegaMenu = {
      menuHeader,
      menuLink,
      backButton,
      hasDropdown,
      onMouseEnter: () => {
        if (!this.isDesktop()) return;
        this.menus.forEach((otherMenu) => {
          if (otherMenu === menu) return;
          otherMenu.classList.remove("active");
          otherMenu.querySelector(".menu-tab__title")?.setAttribute("aria-expanded", "false");
        });
        menu.classList.add("active");
        menuLink?.setAttribute("aria-expanded", "true");
      },
      onHeaderClick: (event) => {
        if (this.isDesktop()) return;
        if (!hasDropdown) return;

        if (event.target.closest("a.menu-tab__title")) {
          event.preventDefault();
        }

        menu.classList.toggle("show-content");
        menuLink?.setAttribute("aria-expanded", menu.classList.contains("show-content").toString());
      },
      onBackClick: () => {
        if (this.isDesktop()) return;
        menu.classList.remove("show-content");
        menuLink?.setAttribute("aria-expanded", "false");
      },
      onKeydown: (event) => {
        if (event.key !== "Escape") return;
        menu.classList.remove("active");
        menu.classList.remove("show-content");
        menuLink?.setAttribute("aria-expanded", "false");
        menuLink?.focus();
      },
    };

    menu.addEventListener("mouseenter", menu._splitMegaMenu.onMouseEnter);
    menu.addEventListener("keydown", menu._splitMegaMenu.onKeydown);
    menuHeader?.addEventListener("click", menu._splitMegaMenu.onHeaderClick);
    backButton?.addEventListener("click", menu._splitMegaMenu.onBackClick);
  }

  unbindMenu(menu) {
    const bound = menu._splitMegaMenu;
    if (!bound) return;

    menu.removeEventListener("mouseenter", bound.onMouseEnter);
    menu.removeEventListener("keydown", bound.onKeydown);
    bound.menuHeader?.removeEventListener("click", bound.onHeaderClick);
    bound.backButton?.removeEventListener("click", bound.onBackClick);
    delete menu._splitMegaMenu;

    this.unbindSubmenuList(menu);
  }

  bindSubmenuList(menu) {
    const navColumn = menu.querySelector(".mega-menu__nav-column");
    const allItems = navColumn
      ? Array.from(navColumn.querySelectorAll(":scope > .mega-menu__list > .mega-menu__list-item"))
      : [];
    const submenuItems = allItems.filter(
      (item) => item.classList.contains("has-submenu") || item.classList.contains("has-hover-promo")
    );
    if (!navColumn || !allItems.length || !submenuItems.length) return;

    // Hovering ANY sibling item (submenu or not) must close whatever submenu is currently open.
    const onItemEnter = (event) => {
      submenuItems.forEach((item) => item.classList.toggle("active", item === event.currentTarget));
    };
    const onColumnLeave = () => {
      submenuItems.forEach((item) => item.classList.remove("active"));
    };

    allItems.forEach((item) => item.addEventListener("mouseenter", onItemEnter));
    navColumn.addEventListener("mouseleave", onColumnLeave);

    menu._submenuList = { navColumn, items: allItems, submenuItems, onItemEnter, onColumnLeave };
  }

  unbindSubmenuList(menu) {
    const bound = menu._submenuList;
    if (!bound) return;

    bound.items.forEach((item) => item.removeEventListener("mouseenter", bound.onItemEnter));
    bound.navColumn.removeEventListener("mouseleave", bound.onColumnLeave);
    delete menu._submenuList;
  }
}

if (!customElements.get("split-megamenu")) {
  customElements.define("split-megamenu", SplitMegaMenu);
}
